import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import StatusBadge from '../components/StatusBadge';
import { STATUTS_LIST } from '../utils/constants';
import { Search, Plus, ChevronRight, RefreshCw, Filter, X, Mail, MailWarning, MailX, User } from 'lucide-react';

const EMAIL_FILTERS = {
  active: { label: 'Email actif', color: 'bg-emerald-100 text-emerald-700', icon: Mail },
  pending: { label: 'Email en attente', color: 'bg-amber-100 text-amber-700', icon: MailWarning },
  none: { label: 'Sans email', color: 'bg-gray-200 text-gray-700', icon: MailX }
};

export default function ClientList() {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [commerciaux, setCommerciaux] = useState([]);
  const [commercialFilter, setCommercialFilter] = useState('');
  const emailFilter = searchParams.get('email_status');
  const navigate = useNavigate();
  const LIMIT = 20;

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: page * LIMIT };
      if (search) params.search = search;
      if (emailFilter) params.email_status = emailFilter;
      if (commercialFilter) params.commercial_id = commercialFilter;
      const res = await api.get('/clients', { params });
      setClients(res.data.clients);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, page, emailFilter, commercialFilter]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  useEffect(() => {
    api.get('/users/commerciaux/list').then(r => setCommerciaux(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setPage(0), 400);
    return () => clearTimeout(t);
  }, [search]);

  const clearEmailFilter = () => {
    searchParams.delete('email_status');
    setSearchParams(searchParams);
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[hsl(222,47%,11%)]">Clients</h1>
          <p className="text-gray-500 text-sm">{total} client{total > 1 ? 's' : ''} au total</p>
        </div>
        <button
          onClick={() => navigate('/clients/nouveau')}
          className="flex items-center gap-2 bg-[hsl(222,47%,11%)] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[hsl(222,47%,18%)] transition-colors active:scale-95"
          data-testid="new-client-btn"
        >
          <Plus size={16} />
          Nouveau client
        </button>
      </div>

      {/* Search + Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par nom, SIRET, ville..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)] bg-white"
            data-testid="client-search-input"
          />
        </div>
        {commerciaux.length > 0 && (
          <select
            value={commercialFilter}
            onChange={e => { setCommercialFilter(e.target.value); setPage(0); }}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
            data-testid="commercial-filter"
          >
            <option value="">Tous les commerciaux</option>
            {commerciaux.map(c => (
              <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>
            ))}
          </select>
        )}
      </div>

      {/* Active Email Filter Badge */}
      {emailFilter && EMAIL_FILTERS[emailFilter] && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Filtre actif :</span>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${EMAIL_FILTERS[emailFilter].color}`}>
            {React.createElement(EMAIL_FILTERS[emailFilter].icon, { size: 14 })}
            {EMAIL_FILTERS[emailFilter].label}
            <button
              onClick={clearEmailFilter}
              className="ml-1 p-0.5 hover:bg-black/10 rounded-full transition-colors"
              data-testid="clear-email-filter"
            >
              <X size={14} />
            </button>
          </span>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <RefreshCw size={20} className="animate-spin text-gray-400" />
          </div>
        ) : clients.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Search size={40} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">Aucun client trouvé</p>
            <p className="text-sm mt-1">Essayez une autre recherche ou créez un nouveau client</p>
            <button
              onClick={() => navigate('/clients/nouveau')}
              className="mt-4 bg-[hsl(222,47%,11%)] text-white px-4 py-2 rounded-lg text-sm hover:bg-[hsl(222,47%,18%)] transition-colors"
              data-testid="create-client-empty-btn"
            >
              Créer un client
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100">
                  <th className="text-left px-5 py-3">Société</th>
                  <th className="text-left px-5 py-3">Commercial</th>
                  <th className="text-left px-5 py-3">Gérant</th>
                  <th className="text-left px-5 py-3">Ville</th>
                  <th className="text-left px-5 py-3">Dernier statut</th>
                  <th className="text-left px-5 py-3">Contact</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {clients.map(client => (
                  <tr
                    key={client.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer group"
                    onClick={() => navigate(`/clients/${client.id}`)}
                    data-testid={`client-row-${client.id}`}
                  >
                    <td className="px-5 py-3.5">
                      <p className="text-sm font-semibold text-gray-900">{client.societe}</p>
                      <p className="text-xs text-gray-400">{client.code_client}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      {client.commercial_prenom || client.commercial_nom ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full">
                          <User size={10} />
                          {client.commercial_prenom} {client.commercial_nom?.[0]}.
                        </span>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-gray-700">{client.gerant || '—'}</td>
                    <td className="px-5 py-3.5 text-sm text-gray-600">{client.ville || '—'}</td>
                    <td className="px-5 py-3.5">
                      {client.last_statut ? <StatusBadge statut={client.last_statut} /> : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-xs text-gray-600">{client.email_salon || '—'}</p>
                      <p className="text-xs text-gray-400">{client.tel_salon || ''}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-600 transition-colors" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > LIMIT && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
            <span>{page * LIMIT + 1} - {Math.min((page + 1) * LIMIT, total)} sur {total}</span>
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-30 hover:bg-gray-50">Précédent</button>
              <button disabled={(page + 1) * LIMIT >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-30 hover:bg-gray-50">Suivant</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
